import { useMemo } from 'react';

import { CollectPhoneStatus } from '../../../artifacts/gql/graphql';
import { useUnifiedUserData } from '../useUnifiedUserData';

export interface UnifiedUserIssue {
  type: 'phone' | 'email' | 'address' | 'kyc' | 'name';
  severity: 'warning' | 'error' | 'info';
  message: string;
  actionRequired: boolean;
  modalType?: string;
}

export const useUnifiedUserStatus = () => {
  const { unifiedUser } = useUnifiedUserData();

  const issues = useMemo((): UnifiedUserIssue[] => {
    if (!unifiedUser) return [];

    const newIssues: UnifiedUserIssue[] = [];

    // Check name completion
    const firstName = unifiedUser.firstName?.trim();
    const lastName = unifiedUser.lastName?.trim();

    if (!firstName || !lastName) {
      newIssues.push({
        type: 'name',
        severity: 'error',
        message: 'First and last name are required',
        actionRequired: true,
        modalType: 'name-completion',
      });
    } else if (firstName.length < 2 || lastName.length < 2) {
      newIssues.push({
        type: 'name',
        severity: 'warning',
        message: 'Please provide your full first and last name',
        actionRequired: true,
        modalType: 'name-completion',
      });
    }

    // Check phone status
    if (unifiedUser.phone) {
      const phoneStatus = unifiedUser.phone.status;

      if (
        !phoneStatus ||
        phoneStatus === CollectPhoneStatus.PhoneStatusUnverified
      ) {
        newIssues.push({
          type: 'phone',
          severity: 'error',
          message: 'Phone number needs to be verified',
          actionRequired: true,
          modalType: 'phone-verification',
        });
      } else if (phoneStatus === CollectPhoneStatus.PhoneStatusCodeSent) {
        newIssues.push({
          type: 'phone',
          severity: 'warning',
          message:
            'Phone verification code sent - please complete verification',
          actionRequired: true,
          modalType: 'phone-verification',
        });
      } else if (phoneStatus === CollectPhoneStatus.PhoneStatusUnverifiable) {
        newIssues.push({
          type: 'phone',
          severity: 'error',
          message:
            'Phone number cannot be verified - please update your phone number',
          actionRequired: true,
          modalType: 'phone-update',
        });
      }
    } else {
      newIssues.push({
        type: 'phone',
        severity: 'error',
        message: 'Phone number is required',
        actionRequired: true,
        modalType: 'phone-setup',
      });
    }

    // Check for phone conflicts
    if (unifiedUser.unifiedPhoneStatus?.hasConflict) {
      newIssues.push({
        type: 'phone',
        severity: 'warning',
        message:
          'Multiple phone numbers found - please select your preferred number',
        actionRequired: true,
        modalType: 'phone-resolution',
      });
    }

    // Check email status
    if (unifiedUser.email) {
      if (!unifiedUser.email.verified) {
        newIssues.push({
          type: 'email',
          severity: 'warning',
          message: 'Email address needs to be verified',
          actionRequired: true,
          modalType: 'email-verification',
        });
      }
    } else {
      newIssues.push({
        type: 'email',
        severity: 'error',
        message: 'Email address is required',
        actionRequired: true,
        modalType: 'email-setup',
      });
    }

    // Check addresses
    if (!unifiedUser.addresses || unifiedUser.addresses.length === 0) {
      newIssues.push({
        type: 'address',
        severity: 'warning',
        message: 'At least one address is required',
        actionRequired: true,
        modalType: 'address-setup',
      });
    }

    // Check KYC status
    if (unifiedUser.kycStatus) {
      // Add KYC status checks here when needed
    }

    return newIssues;
  }, [unifiedUser]);

  const hasIssues = useMemo(() => issues.length > 0, [issues]);

  const hasCriticalIssues = useMemo(
    () => issues.some((issue) => issue.severity === 'error'),
    [issues]
  );

  const hasActionableIssues = useMemo(
    () => issues.some((issue) => issue.actionRequired),
    [issues]
  );

  const getPhoneStatus = () => {
    return unifiedUser?.phone?.status;
  };

  const getEmailStatus = () => {
    return unifiedUser?.email?.verified;
  };

  const getAddressCount = () => {
    return unifiedUser?.addresses?.length || 0;
  };

  const getNameStatus = () => {
    const firstName = unifiedUser?.firstName?.trim();
    const lastName = unifiedUser?.lastName?.trim();
    return {
      hasFirstName: !!firstName && firstName.length >= 2,
      hasLastName: !!lastName && lastName.length >= 2,
      isComplete: !!(
        firstName &&
        lastName &&
        firstName.length >= 2 &&
        lastName.length >= 2
      ),
    };
  };

  const getPhoneConflictStatus = () => {
    return {
      hasConflict: unifiedUser?.unifiedPhoneStatus?.hasConflict || false,
      livePhone: unifiedUser?.unifiedPhoneStatus?.livePhoneNumber,
      collectPhone: unifiedUser?.unifiedPhoneStatus?.collectPhoneNumber,
    };
  };

  return {
    issues,
    hasIssues,
    hasCriticalIssues,
    hasActionableIssues,
    getPhoneStatus,
    getEmailStatus,
    getAddressCount,
    getNameStatus,
    getPhoneConflictStatus,
    unifiedUser,
  };
};
